import {
  CHAT_ATTACHMENT_CATEGORY_COLORS,
  CHAT_ATTACHMENT_DOCUMENT_CATEGORIES,
} from '~/constants/chatAttachmentDocumentCategories'
import type { ChatAttachmentDocumentCategory, ChatAttachmentRequest } from '~/types/ChatAttachment'

const RELATIVE_TIME_DIVISORS: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
  { unit: 'year', ms: 1000 * 60 * 60 * 24 * 365 },
  { unit: 'month', ms: 1000 * 60 * 60 * 24 * 30 },
  { unit: 'week', ms: 1000 * 60 * 60 * 24 * 7 },
  { unit: 'day', ms: 1000 * 60 * 60 * 24 },
  { unit: 'hour', ms: 1000 * 60 * 60 },
  { unit: 'minute', ms: 1000 * 60 },
]

/**
 * Indique si le type MIME correspond à une image.
 * @param {string} contentType - Type MIME du fichier.
 * @returns {boolean} `true` si le fichier est une image.
 */
export function isChatAttachmentImageContentType(contentType: string): boolean {
  return contentType.startsWith('image/')
}

/**
 * Retourne la couleur d'accent d'une catégorie documentaire.
 * @param {string} documentCategory - Catégorie renvoyée par l'API.
 * @returns {string} Couleur hexadécimale.
 */
export function getChatAttachmentCategoryColor(documentCategory: string): string {
  if (
    CHAT_ATTACHMENT_DOCUMENT_CATEGORIES.includes(documentCategory as ChatAttachmentDocumentCategory)
  ) {
    return CHAT_ATTACHMENT_CATEGORY_COLORS[documentCategory as ChatAttachmentDocumentCategory]
  }
  return CHAT_ATTACHMENT_CATEGORY_COLORS.Other
}

/**
 * Formate une date ISO en libellé relatif localisé (ex. « il y a 12 min »).
 * @param {string | undefined} createdAt - Date ISO renvoyée par l'API.
 * @param {string} locale - Locale i18n active (ex. `fr`, `en`).
 * @returns {string | null} Libellé relatif ou `null` si la date est absente/invalide.
 */
export function formatChatAttachmentRelativeTime(
  createdAt: string | undefined,
  locale: string,
): string | null {
  if (!createdAt) return null

  const parsedDate: Date = new Date(createdAt)
  if (Number.isNaN(parsedDate.getTime())) return null

  const diffMs: number = parsedDate.getTime() - Date.now()
  const formatter: Intl.RelativeTimeFormat = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
  })

  for (const { unit, ms } of RELATIVE_TIME_DIVISORS) {
    const value: number = Math.round(diffMs / ms)
    if (Math.abs(value) >= 1 || unit === 'minute') {
      return formatter.format(value, unit)
    }
  }

  return formatter.format(0, 'minute')
}

/**
 * Lit un fichier et le convertit en chaîne base64 (sans préfixe data URL).
 * @param {File} file - Fichier à lire.
 * @returns {Promise<string>} Le contenu encodé en base64.
 * @throws {Error} Rejette si la lecture du fichier échoue.
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise<string>(
    (resolve: (base64: string) => void, reject: (error: Error) => void): void => {
      const reader: FileReader = new FileReader()
      reader.onload = (): void => {
        const dataUrl: string = reader.result as string
        const base64SeparatorIndex: number = dataUrl.indexOf(',')
        resolve(base64SeparatorIndex >= 0 ? dataUrl.slice(base64SeparatorIndex + 1) : dataUrl)
      }
      reader.onerror = (): void =>
        reject(reader.error ?? new Error('Lecture du fichier impossible'))
      reader.readAsDataURL(file)
    },
  )
}

/**
 * Sérialise des fichiers pour le body JSON de l'API conversation.
 * @param {File[]} files - Pièces jointes à sérialiser.
 * @returns {Promise<ChatAttachmentRequest[]>} Les pièces jointes prêtes pour l'envoi.
 */
export function buildAttachmentsPayload(files: File[]): Promise<ChatAttachmentRequest[]> {
  return Promise.all(
    files.map(
      async (file: File): Promise<ChatAttachmentRequest> => ({
        fileName: file.name,
        contentType: file.type,
        documentCategory: '',
        data: await readFileAsBase64(file),
      }),
    ),
  )
}
